/**
 * ⓘ beside a term a teammate may not know: DataPack, D-Plan, a panel's name, a portfolio figure.
 *
 * Opens on hover, on keyboard focus, or on a click (so a phone can open it too), and closes on
 * Escape or a click elsewhere. The popover is absolutely positioned, so opening it never moves
 * anything around it. The drawn icon is 16px; the button is the 40px target the size audit asks
 * for, pulled back with negative margins so it takes no more room in the line than the icon.
 */

import { useEffect, useRef, useState } from "react";
import { useT } from "../../i18n/useT";

export function Info({ term, text }: { term: string; text: string }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  return (
    <span className="info" ref={box} data-open={open ? "" : undefined}>
      <button
        type="button"
        className="info__btn"
        aria-label={t("info.what", { term })}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
            event.currentTarget.blur();
          }
        }}
      >
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.3" />
          <path d="M8 7v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="4.6" r="0.95" fill="currentColor" />
        </svg>
      </button>
      <span className="info__pop" role="tooltip">
        {text}
      </span>
    </span>
  );
}
